import { sanitizeApiText } from '~/utils/helpers'

const EMPTY_PARAGRAPH_PATTERNS: readonly RegExp[] = [
  /<p\b[^>]*>\s*<\/p>/gi,
  /<p\b[^>]*>(?:\s|&nbsp;|&#(?:160|xA0);|<br\s*\/?>)*<\/p>/gi,
  /<p\b[^>]*>(?:\s|<(?!\/p\b)[a-z][^>]*>\s*<\/[a-z][^>]*>\s*)*<\/p>/gi,
]

const BLOCK_HTML_PATTERN = /<\s*(p|div|h[1-6]|ul|ol|li|br)\b/i

const QL_EDITOR_WRAPPER_PATTERN =
  /^<div\b[^>]*\bclass=(["'])[^"']*\bql-editor\b[^"']*\1[^>]*>([\s\S]*)<\/div>\s*$/i

/**
 * Indique si la chaîne ressemble à du HTML Quill (blocs) plutôt qu'à du texte brut.
 * @param {string} html Chaîne à analyser.
 * @returns {boolean} `true` si des balises de bloc sont détectées.
 */
function looksLikeQuillHtml(html: string): boolean {
  return BLOCK_HTML_PATTERN.test(html)
}

/**
 * Normalise les retours ligne : texte brut → `<br>`, HTML Quill → suppression des `\n`.
 * @param {string} html HTML ou texte brut.
 * @returns {string} Chaîne sans `\r` parasites.
 */
function normalizeLineBreaks(html: string): string {
  let result: string = sanitizeApiText(html)
    .replace(/\r<br>/gi, '<br>')
    .replace(/\r/g, '')

  if (looksLikeQuillHtml(result)) {
    result = result.replace(/\n/g, '')
  } else {
    result = result.replace(/\n/g, '<br>')
  }

  return result
}

/**
 * Retire les wrappers racine `.ql-editor` imbriqués (évite le double conteneur au rendu).
 * @param {string} html HTML potentiellement encapsulé.
 * @returns {string} HTML sans wrapper Quill redondant.
 */
function unwrapQuillEditorWrapper(html: string): string {
  let result: string = html.trim()
  let previous: string = ''

  while (result !== previous) {
    previous = result
    const wrapped: RegExpMatchArray | null = result.match(QL_EDITOR_WRAPPER_PATTERN)
    if (wrapped?.[2]) {
      result = wrapped[2].trim()
    }
  }

  return result
}

/**
 * Supprime les paragraphes vides (espaces, `&nbsp;`, `<br>` seuls).
 * @param {string} html HTML Quill.
 * @returns {string} HTML sans `<p>` vides.
 */
function removeEmptyParagraphs(html: string): string {
  let result: string = html.replace(/\uFEFF|\u200B/g, '')
  let previous: string = ''

  while (result !== previous) {
    previous = result
    for (const pattern of EMPTY_PARAGRAPH_PATTERNS) {
      result = result.replace(pattern, '')
    }
  }

  return result
}

/**
 * Réduit les `<br>` consécutifs et ceux autour des titres / paragraphes.
 * @param {string} html HTML Quill.
 * @returns {string} HTML avec espacements verticaux allégés.
 */
function collapseRedundantBreaks(html: string): string {
  return html
    .replace(/(?:<br\s*\/?>\s*){2,}/gi, '<br>')
    .replace(/(<\/h[1-6]>)\s*(?:<br\s*\/?>\s*)+/gi, '$1')
    .replace(/(?:<br\s*\/?>\s*)+(<h[1-6]\b)/gi, '$1')
    .replace(/(<\/p>)\s*<br\s*\/?>\s*(?=<p\b)/gi, '$1')
    .replace(/^(?:\s*<br\s*\/?>\s*)+/i, '')
    .replace(/(?:<br\s*\/?>\s*)+$/i, '')
}

const NBSP_ENTITY_PATTERN = /(?:&nbsp;|&#160;|&#xA0;|\u00A0)/gi

/**
 * Réduit les `&nbsp;` consécutifs (et autres espaces insécables) en un seul.
 * @param {string} html HTML Quill.
 * @returns {string} HTML avec espacements horizontaux normalisés.
 */
function collapseRedundantNbsp(html: string): string {
  return html.replace(new RegExp(`(${NBSP_ENTITY_PATTERN.source}){2,}`, 'gi'), '&nbsp;')
}

/**
 * Nettoie le HTML issu de Quill (structure, espacements, wrappers).
 * @param {string} html Contenu HTML brut.
 * @returns {string} HTML prêt pour `v-html`.
 */
export function normalizeQuillHtml(html: string): string {
  if (!html) return ''

  let result: string = normalizeLineBreaks(html)
  result = unwrapQuillEditorWrapper(result)
  result = removeEmptyParagraphs(result)
  result = collapseRedundantBreaks(result)
  result = collapseRedundantNbsp(result)
  result = removeEmptyParagraphs(result)

  return result.trim()
}
